import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playSound, speak } from '../utils/sounds';
import { getApiUrl, getExerciseEndpoint } from '../config/api';
import { getSuccessMessage, getErrorMessage } from '../utils/encouragements';
import RewardCounter from '../components/RewardCounter';

const MathExercise = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const hasSpokenRef = React.useRef(false);

  const loadExercise = useCallback(async () => {
    try {
      const endpoint = getExerciseEndpoint('math', level);
      const url = getApiUrl(endpoint);
      const response = await axios.get(url);
      setExercise(response.data);
      setSelectedAnswer(null);
      setFeedback(null);
      setHasAnswered(false);
      hasSpokenRef.current = false; // Réinitialiser pour le nouvel exercice
      setTimeout(() => {
        if (!hasSpokenRef.current) {
          speak(response.data.instructions);
          setTimeout(() => {
            if (response.data.question) {
              speak(response.data.question.replace('+', 'plus').replace('-', 'moins'));
            } else if (response.data.audioText) {
              speak(response.data.audioText);
            }
          }, 2000);
          hasSpokenRef.current = true;
        }
      }, 500);
    } catch (error) {
      console.error('Error loading exercise:', error);
    }
  }, [level]);

  useEffect(() => {
    loadExercise();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  const handleReplaySound = () => {
    if (exercise) {
      playSound('click');
      if (exercise.question) {
        speak(exercise.question.replace('+', 'plus').replace('-', 'moins'));
      } else if (exercise.audioText) {
        speak(exercise.audioText);
      }
    }
  };

  const handleAnswerClick = (option) => {
    if (hasAnswered) return;

    playSound('click');
    setSelectedAnswer(option);
    const isCorrect = option === exercise.correctAnswer;

    setHasAnswered(true);

    if (isCorrect) {
      playSound('success');
      setCorrectCount(prev => prev + 1);
      const childName = localStorage.getItem('childName');
      const successMessage = getSuccessMessage(childName);
      setFeedback({
        type: 'success',
        message: '🎉 ✨ Bravo ! C\'est la bonne réponse ! ⭐'
      });
      speak(successMessage);
      setTimeout(() => {
        loadExercise();
      }, 3000);
    } else {
      playSound('error');
      setIncorrectCount(prev => prev + 1);
      const childName = localStorage.getItem('childName');
      const errorMessage = getErrorMessage(childName);
      const fullMessage = `${errorMessage} La bonne réponse était ${exercise.correctAnswer}`;
      setFeedback({
        type: 'error',
        message: `❌ Pas tout à fait ! La bonne réponse était "${exercise.correctAnswer}".`
      });
      speak(fullMessage);
      // Calculer le délai en fonction de la longueur du message (environ 100ms par caractère)
      const delay = Math.max(5000, fullMessage.length * 100);
      setTimeout(() => {
        loadExercise();
      }, delay);
    }
  };

  if (!exercise) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div>
      <RewardCounter correctCount={correctCount} incorrectCount={incorrectCount} />

      <div className="header">
        <h1>🔢 Mathématiques - Niveau {level}</h1>
        <p>{exercise.instructions}</p>
      </div>

      <div className="card">
        {exercise.question && (
          <div className={`question-display ${feedback?.type === 'success' ? 'celebrate' : feedback?.type === 'error' ? 'shake' : ''}`}>
            {exercise.question} = ?
          </div>
        )}

        {exercise.audioText && (
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <button
              className="btn btn-success bounce"
              onClick={handleReplaySound}
              style={{ fontSize: '2rem', padding: '25px 50px' }}
            >
              🔊 Écoute le nombre
            </button>
          </div>
        )}

        {feedback && (
          <div className={`feedback ${feedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`}>
            {feedback.message}
          </div>
        )}

        <div className="options-grid">
          {exercise.options.map((option, index) => {
            let buttonClass = 'btn option-btn btn-primary';
            if (hasAnswered) {
              if (option === exercise.correctAnswer) {
                buttonClass = 'btn option-btn btn-success celebrate';
              } else if (option === selectedAnswer) {
                buttonClass = 'btn option-btn btn-danger shake';
              } else {
                buttonClass = 'btn option-btn';
              }
            }

            return (
              <button
                key={index}
                className={buttonClass}
                onClick={() => handleAnswerClick(option)}
                disabled={hasAnswered}
                style={{
                  opacity: hasAnswered && option !== exercise.correctAnswer && option !== selectedAnswer ? 0.5 : 1
                }}
              >
                {option}
              </button>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: '40px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          <button
            className="btn btn-warning"
            onClick={() => navigate('/subjects')}
          >
            🏠 Retour
          </button>
          <button
            className="btn btn-success"
            onClick={loadExercise}
          >
            ⏭️ Suivant
          </button>
        </div>
      </div>
    </div>
  );
};

export default MathExercise;
